import { z } from 'zod';

// Public, non-secret environment values that are safe to evaluate anywhere —
// server runtime, `next build`, and the browser bundle. Every field is
// defaulted or optional, so parsing can never fail in a context where the
// variables aren't set (e.g. the browser, where only NEXT_PUBLIC_* values
// exist).
//
// Server-only secrets live in `env.server.ts`, which extends this schema.
export const clientEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'production', 'test']).optional(),
  // Base URL of the backend. On the server this is the real (internal)
  // backend URL; in the browser the variable isn't inlined (no NEXT_PUBLIC_
  // prefix), so the default applies.
  API_URL: z.url().default('http://localhost:3000'),
  // Browser-side backend base URL, inlined into the client bundle at
  // `next build`. In production: `https://lokal-api.schuelerhilfe.de`
  // (through Cloudflare). In local dev: `http://localhost:3000`.
  // Browser calls go directly to the backend — no proxy routes.
  NEXT_PUBLIC_API_URL: z.url().default('http://localhost:3000'),
  NEXT_PUBLIC_STRAPI_URL: z.url().default('http://localhost:1337'),
  IS_CANARY: z.stringbool().default(false),
  COMMIT_SHA: z.string().default('unknown'),
});

export type ClientEnvironmentType = z.infer<typeof clientEnvSchema>;

let cached: ClientEnvironmentType | undefined;

function parseEnv(): ClientEnvironmentType {
  if (!cached) {
    // Explicit member expressions (not a `process.env` object spread) so
    // Next.js can inline NEXT_PUBLIC_* values into the client bundle —
    // bundlers only replace literal `process.env.NEXT_PUBLIC_X` accesses.
    cached = clientEnvSchema.parse({
      NODE_ENV: process.env.NODE_ENV,
      API_URL: process.env.API_URL,
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
      NEXT_PUBLIC_STRAPI_URL: process.env.NEXT_PUBLIC_STRAPI_URL,
      IS_CANARY: process.env.IS_CANARY,
      COMMIT_SHA: process.env.COMMIT_SHA,
    });
  }
  return cached;
}

/**
 * Reset the cached parsed environment. Intended for tests that mutate
 * process.env between cases.
 */
export function resetClientEnvCache(): void {
  cached = undefined;
}

// Lazy proxy: parsing happens on first property access, not at module load.
// Behaviorally equivalent to an eager parse (everything is defaulted), but
// keeps the cache resettable for tests that mutate process.env.
export const ClientEnvironment = new Proxy({} as ClientEnvironmentType, {
  get: (_target, prop) => parseEnv()[prop as keyof ClientEnvironmentType],
  has: (_target, prop) => prop in parseEnv(),
  ownKeys: () => Object.keys(parseEnv()),
  getOwnPropertyDescriptor: (_target, prop) => ({
    enumerable: true,
    configurable: true,
    value: parseEnv()[prop as keyof ClientEnvironmentType],
  }),
});
